import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Stack,
  Alert,
  useTheme,
  alpha,
  Snackbar,
  useMediaQuery
} from '@mui/material';
import { useCryptoFormValidation, useCryptoOTPManagement, useCryptoWalletForm } from './hooks';
import { CryptoModalHeader, CryptoWalletForm, OTPVerification, CryptoModalActions } from './components';
import { fetchPaymentMethods } from '../../services/backendAPI';

const AddCryptoWalletModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [cryptoCurrencies, setCryptoCurrencies] = useState([]);

  const { errors, validateForm, clearError, setErrors: setError } = useCryptoFormValidation();
  const {
    otpSent,
    isLoading: otpLoading,
    timer,
    canResendOTP,
    snackbarMessage,
    snackbarSeverity,
    showSnackbar,
    setShowSnackbar,
    handleSendOTP,
    resetOTPState
  } = useCryptoOTPManagement();

  const {
    formData,
    loading,
    handleChange,
    resetForm,
    handleSubmit
  } = useCryptoWalletForm();

  useEffect(() => {
      if (open) {
          const loadCurrencies = async () => {
              try {
                  const res = await fetchPaymentMethods();
                  let dataToProcess = [];
                  
                  // Handle exact nested response structure
                  if (res?.status === 'success' && res?.data?.currencies) {
                      dataToProcess = res.data.currencies;
                  } else if (Array.isArray(res)) {
                      dataToProcess = res;
                  } else if (Array.isArray(res?.data)) {
                      dataToProcess = res.data;
                  }

                  if (dataToProcess && dataToProcess.length > 0) {
                      const cryptos = dataToProcess.filter(c => c.kind === 'crypto');
                      setCryptoCurrencies(cryptos);
                  }
              } catch (error) {
                  console.error("Failed to load crypto currencies", error);
              }
          };
          loadCurrencies();
          // Auto-send OTP when modal opens to mirror Bank Addition behavior
          handleSendOTP();
      } else {
          resetOTPState();
          resetForm();
      }
  }, [open, handleSendOTP, resetOTPState, resetForm]);

  const handleFormChange = (e) => {
    handleChange(e, clearError);
  };

  const handleFormSubmit = async () => {
    const success = await handleSubmit(validateForm, (type, msg) => {
        setError(prev => ({ ...prev, [type]: msg }));
    }, otpSent, cryptoCurrencies);
    
    if (success) {
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            maxWidth: 500,
            width: '100%',
            height: isMobile ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <CryptoModalHeader onClose={onClose} />

        <DialogContent sx={{
          p: isMobile ? 2 : 3,
          pt: 2,
          flex: isMobile ? 1 : 'initial',
          overflow: 'auto'
        }}>
          <Stack spacing={isMobile ? 2.5 : 3}>
            {errors.submit && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                }}
              >
                {errors.submit}
              </Alert>
            )}

            <CryptoWalletForm
              formData={formData}
              errors={errors}
              onChange={handleFormChange}
              cryptoCurrencies={cryptoCurrencies}
            />

            <OTPVerification
              formData={formData}
              errors={errors}
              timer={timer}
              otpSent={otpSent}
              isLoading={otpLoading}
              canResendOTP={canResendOTP}
              onChange={handleFormChange}
              onSendOTP={handleSendOTP}
              size="small"
            />
          </Stack>
        </DialogContent>

        <CryptoModalActions
          onClose={onClose}
          onSubmit={handleFormSubmit}
          loading={loading}
        />
      </Dialog>

      {/* Snackbar for OTP notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'center' : 'right'
        }}
        sx={{ 
            zIndex: (theme) => theme.zIndex.modal + 2000,
            mt: isMobile ? 2 : 0 
        }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
              width: isMobile ? '80%' : '100%', 
              maxWidth: '400px', 
              boxShadow: 3,
              mx: 'auto'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddCryptoWalletModal;
